import { cn } from '@/lib/utils';
import * as agentService from '@/services/ai-agent.service';
import { useAiAgentStore } from '@/store/aiAgentStore';
import type { ActionRisk, AiAction, AiActionStatus, AiAuthorityTier } from '@/types/ai-authority';
import { AI_TIER_DESCRIPTIONS, AI_TIER_NAMES, BUILT_IN_WORKFLOWS } from '@/types/ai-authority';
import {
    Activity,
    Ban,
    Brain,
    Bug,
    CheckCircle2,
    ChevronDown,
    ChevronRight,
    Clock,
    DollarSign,
    Eye,
    Hammer,
    Loader2,
    Lock,
    Play,
    Rocket,
    RotateCcw,
    Settings2,
    ShieldAlert,
    ShieldCheck,
    ShieldOff,
    Sparkles,
    XCircle,
    Zap,
} from 'lucide-react';
import { useState } from 'react';

/* ================================================================== */
/*  AI Governance Panel                                               */
/*                                                                    */
/*  The control center for the autonomous AI agent.                   */
/*  Manage authority tier, view pending approvals, monitor actions,    */
/*  configure policies, and engage the kill switch.                   */
/* ================================================================== */

/* -- Risk badge ---------------------------------------------------- */

const RISK_COLORS: Record<ActionRisk, string> = {
  low: 'text-green-400 bg-green-400/10 border-green-400/20',
  medium: 'text-yellow-400 bg-yellow-400/10 border-yellow-400/20',
  high: 'text-orange-400 bg-orange-400/10 border-orange-400/20',
  critical: 'text-red-400 bg-red-400/10 border-red-400/20',
};

function RiskBadge({ risk }: { risk: ActionRisk }) {
  return (
    <span
      className={cn(
        'px-1.5 py-0.5 text-[9px] font-semibold uppercase rounded border',
        RISK_COLORS[risk],
      )}
    >
      {risk}
    </span>
  );
}

/* -- Status badge -------------------------------------------------- */

const STATUS_CONFIG: Partial<Record<AiActionStatus, { icon: typeof CheckCircle2; color: string }>> =
  {
    proposed: { icon: Clock, color: 'text-blue-400' },
    approved: { icon: CheckCircle2, color: 'text-green-400' },
    executing: { icon: Loader2, color: 'text-yellow-400' },
    completed: { icon: CheckCircle2, color: 'text-green-400' },
    failed: { icon: XCircle, color: 'text-red-400' },
    rejected: { icon: Ban, color: 'text-[#666]' },
    rolled_back: { icon: RotateCcw, color: 'text-orange-400' },
    expired: { icon: Clock, color: 'text-[#555]' },
  };

function StatusIcon({ status }: { status: AiActionStatus }) {
  const cfg = STATUS_CONFIG[status];
  if (!cfg) return null;
  const Icon = cfg.icon;
  return (
    <Icon className={cn('w-3.5 h-3.5', cfg.color, status === 'executing' && 'animate-spin')} />
  );
}

/* -- Tier selector ------------------------------------------------- */

function TierSelector() {
  const tier = useAiAgentStore((s) => s.policy.tier);
  const setTier = useAiAgentStore((s) => s.setTier);
  const killSwitch = useAiAgentStore((s) => s.policy.killSwitch);

  const tiers: AiAuthorityTier[] = [1, 2, 3, 4];

  return (
    <div className="px-3 py-2 border-b border-[#2d2d2d]">
      <div className="flex items-center gap-2 mb-2">
        <Brain className="w-4 h-4 text-purple-400" />
        <span className="text-[11px] font-semibold text-white">Authority Tier</span>
      </div>

      <div className="grid grid-cols-4 gap-1">
        {tiers.map((t) => (
          <button
            key={t}
            onClick={() => !killSwitch && setTier(t)}
            disabled={killSwitch}
            className={cn(
              'flex flex-col items-center py-1.5 rounded text-[9px] border transition-all',
              tier === t
                ? 'border-purple-400 bg-purple-400/10 text-purple-300'
                : 'border-[#3e3e3e] text-[#888] hover:border-[#555] hover:text-white',
              killSwitch && 'opacity-40 cursor-not-allowed',
            )}
          >
            <span className="font-bold text-[11px]">{t}</span>
            <span className="truncate w-full text-center">{AI_TIER_NAMES[t]}</span>
          </button>
        ))}
      </div>

      <p className="text-[10px] text-[#666] mt-1.5">{AI_TIER_DESCRIPTIONS[tier]}</p>
    </div>
  );
}

/* -- Agent status bar ---------------------------------------------- */

function AgentStatusBar() {
  const policy = useAiAgentStore((s) => s.policy);
  const actionsThisHour = useAiAgentStore((s) => s.actionsThisHour);
  const spentToday = useAiAgentStore((s) => s.spentToday);
  const pendingCount = useAiAgentStore((s) => s.pendingActions.length);
  const executingCount = useAiAgentStore((s) => s.executingActions.length);
  const engage = useAiAgentStore((s) => s.engageKillSwitch);
  const disengage = useAiAgentStore((s) => s.disengageKillSwitch);

  return (
    <div className="px-3 py-2 border-b border-[#2d2d2d]">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          {policy.killSwitch ? (
            <ShieldOff className="w-4 h-4 text-red-400" />
          ) : (
            <ShieldCheck className="w-4 h-4 text-green-400" />
          )}
          <span className="text-[11px] font-semibold text-white">
            Agent {policy.killSwitch ? 'Disabled' : 'Active'}
          </span>
        </div>

        <button
          onClick={() => (policy.killSwitch ? disengage() : engage())}
          className={cn(
            'px-2 py-0.5 rounded text-[10px] font-semibold transition-colors border',
            policy.killSwitch
              ? 'text-green-400 border-green-400/30 hover:bg-green-400/10'
              : 'text-red-400 border-red-400/30 hover:bg-red-400/10',
          )}
        >
          {policy.killSwitch ? 'Enable Agent' : 'Kill Switch'}
        </button>
      </div>

      <div className="grid grid-cols-2 gap-2 text-[10px]">
        <div className="flex items-center gap-1.5 text-[#999]">
          <Zap className="w-3 h-3" />
          <span>
            Rate: {actionsThisHour}/{policy.maxActionsPerHour}/hr
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[#999]">
          <DollarSign className="w-3 h-3" />
          <span>
            Budget: ${spentToday.toFixed(2)}/${policy.maxDailyBudget}
          </span>
        </div>
        <div className="flex items-center gap-1.5 text-[#999]">
          <Clock className="w-3 h-3" />
          <span>Pending: {pendingCount}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[#999]">
          <Activity className="w-3 h-3" />
          <span>Executing: {executingCount}</span>
        </div>
      </div>
    </div>
  );
}

/* -- Pending approvals --------------------------------------------- */

function PendingApprovalsSection() {
  const pending = useAiAgentStore((s) => s.pendingActions);
  const [expanded, setExpanded] = useState(true);

  if (pending.length === 0) return null;

  return (
    <div className="border-b border-[#2d2d2d]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold text-yellow-400 hover:bg-[#2a2d2e] transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        <ShieldAlert className="w-3.5 h-3.5" />
        <span>Pending Approval ({pending.length})</span>
      </button>

      {expanded && (
        <div className="pb-1">
          {pending.map((action) => (
            <ActionApprovalCard key={action.id} action={action} />
          ))}
        </div>
      )}
    </div>
  );
}

function ActionApprovalCard({ action }: { action: AiAction }) {
  return (
    <div className="mx-2 mb-1.5 p-2 bg-[#1e1e1e] rounded border border-[#3e3e3e]">
      <div className="flex items-start justify-between mb-1">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1.5">
            <RiskBadge risk={action.risk} />
            <span className="text-[11px] text-white font-medium truncate">{action.title}</span>
          </div>
          <p className="text-[10px] text-[#888] mt-0.5 line-clamp-2">{action.description}</p>
        </div>
      </div>

      {/* Affected files */}
      {action.affectedFiles && action.affectedFiles.length > 0 && (
        <div className="text-[9px] text-[#666] mb-1.5">
          Files: {action.affectedFiles.join(', ')}
        </div>
      )}

      {/* Steps preview */}
      <div className="text-[9px] text-[#666] mb-2">
        {action.plan.length} step{action.plan.length !== 1 ? 's' : ''}:{' '}
        {action.plan.map((s) => s.description).join(' → ')}
      </div>

      {/* Action buttons */}
      <div className="flex items-center gap-1.5">
        <button
          onClick={() => agentService.approveAction(action.id, 'user', 'Manual approval')}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-green-500/10 text-green-400 border border-green-500/20 hover:bg-green-500/20 transition-colors"
        >
          <CheckCircle2 className="w-3 h-3" />
          Approve
        </button>
        <button
          onClick={() => agentService.rejectAction(action.id, 'User rejected')}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium bg-red-500/10 text-red-400 border border-red-500/20 hover:bg-red-500/20 transition-colors"
        >
          <XCircle className="w-3 h-3" />
          Reject
        </button>
        <button
          onClick={() => {}}
          className="flex items-center gap-1 px-2 py-0.5 rounded text-[10px] text-[#888] border border-[#3e3e3e] hover:text-white hover:border-[#555] transition-colors"
        >
          <Eye className="w-3 h-3" />
          Details
        </button>
      </div>
    </div>
  );
}

/* -- Action history ------------------------------------------------ */

function ActionHistorySection() {
  const history = useAiAgentStore((s) => s.actionHistory);
  const executing = useAiAgentStore((s) => s.executingActions);
  const [expanded, setExpanded] = useState(true);

  const allActions = [...executing, ...history].slice(0, 50);

  return (
    <div className="border-b border-[#2d2d2d]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold text-[#ccc] hover:bg-[#2a2d2e] transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        <Activity className="w-3.5 h-3.5" />
        <span>Action History ({allActions.length})</span>
      </button>

      {expanded && (
        <div className="pb-1 max-h-[300px] overflow-y-auto">
          {allActions.length === 0 ? (
            <p className="px-3 py-4 text-[11px] text-[#666] text-center">
              No actions yet. The AI agent will show its work here.
            </p>
          ) : (
            allActions.map((action) => <ActionHistoryRow key={action.id} action={action} />)
          )}
        </div>
      )}
    </div>
  );
}

function ActionHistoryRow({ action }: { action: AiAction }) {
  const [expanded, setExpanded] = useState(false);
  const time = new Date(action.proposedAt);
  const timeStr = time.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
  });

  return (
    <div className="mx-2 mb-0.5">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-1.5 px-2 py-1 rounded hover:bg-[#2a2d2e] transition-colors text-left"
      >
        <StatusIcon status={action.status} />
        <span className="text-[10px] text-[#555] w-[36px] shrink-0">{timeStr}</span>
        <RiskBadge risk={action.risk} />
        <span className="text-[11px] text-[#ccc] flex-1 truncate">{action.title}</span>

        {action.status === 'completed' && action.rollback?.canRollback && (
          <button
            onClick={(e) => {
              e.stopPropagation();
              agentService.rollbackAction(action.id);
            }}
            className="p-0.5 text-[#666] hover:text-orange-400 transition-colors"
            title="Rollback"
          >
            <RotateCcw className="w-3 h-3" />
          </button>
        )}
      </button>

      {expanded && (
        <div className="ml-6 pl-2 border-l border-[#3e3e3e] mb-1 text-[10px]">
          <p className="text-[#888] mb-1">{action.description}</p>

          {/* Steps */}
          {action.plan.map((step) => (
            <div key={step.id} className="flex items-center gap-1 py-0.5 text-[#666]">
              <span className="w-3 text-center">
                {step.status === 'completed' ? '✓' : step.status === 'failed' ? '✗' : '·'}
              </span>
              <span
                className={cn(
                  step.status === 'completed' && 'text-green-400/70',
                  step.status === 'failed' && 'text-red-400/70',
                )}
              >
                {step.description}
              </span>
            </div>
          ))}

          {/* Result */}
          {action.result && (
            <div
              className={cn(
                'mt-1 px-2 py-1 rounded text-[9px]',
                action.result.success
                  ? 'bg-green-400/5 text-green-400'
                  : 'bg-red-400/5 text-red-400',
              )}
            >
              {action.result.summary}
              {action.result.durationMs > 0 && ` (${action.result.durationMs}ms)`}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* -- Workflows ----------------------------------------------------- */

const WORKFLOW_ICONS: Record<string, typeof Sparkles> = {
  scaffold: Sparkles,
  deploy: Rocket,
  audit: Bug,
  optimize: Zap,
  maintain: Hammer,
};

function WorkflowsSection() {
  const tier = useAiAgentStore((s) => s.policy.tier);
  const killSwitch = useAiAgentStore((s) => s.policy.killSwitch);
  const [expanded, setExpanded] = useState(false);

  const available = BUILT_IN_WORKFLOWS.filter((w) => w.minTier <= tier);

  return (
    <div className="border-b border-[#2d2d2d]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold text-[#ccc] hover:bg-[#2a2d2e] transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        <Sparkles className="w-3.5 h-3.5" />
        <span>
          Workflows ({available.length}/{BUILT_IN_WORKFLOWS.length})
        </span>
      </button>

      {expanded && (
        <div className="pb-1">
          {BUILT_IN_WORKFLOWS.map((wf) => {
            const Icon = WORKFLOW_ICONS[wf.category] ?? Sparkles;
            const isAvailable = wf.minTier <= tier;

            return (
              <div
                key={wf.id}
                className={cn(
                  'mx-2 mb-1 p-2 rounded border',
                  isAvailable
                    ? 'border-[#3e3e3e] bg-[#1e1e1e]'
                    : 'border-[#2d2d2d] bg-[#1a1a1a] opacity-50',
                )}
              >
                <div className="flex items-center gap-1.5 mb-0.5">
                  <Icon className="w-3 h-3 text-purple-400" />
                  <span className="text-[11px] text-white font-medium">{wf.name}</span>
                  <RiskBadge risk={wf.risk} />
                  {!isAvailable && (
                    <span className="text-[8px] text-[#666] border border-[#444] rounded px-1">
                      Tier {wf.minTier}+
                    </span>
                  )}
                </div>
                <p className="text-[10px] text-[#888] mb-1.5">{wf.description}</p>

                {isAvailable && (
                  <button
                    disabled={killSwitch}
                    onClick={() => agentService.startWorkflow(wf.id)}
                    className={cn(
                      'flex items-center gap-1 px-2 py-0.5 rounded text-[10px] font-medium transition-colors',
                      killSwitch
                        ? 'text-[#555] border border-[#333] cursor-not-allowed'
                        : 'text-purple-400 border border-purple-400/20 bg-purple-400/5 hover:bg-purple-400/10',
                    )}
                  >
                    <Play className="w-3 h-3" />
                    Run Workflow
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/* -- Policy settings ----------------------------------------------- */

function PolicySection() {
  const policy = useAiAgentStore((s) => s.policy);
  const updatePolicy = useAiAgentStore((s) => s.updatePolicy);
  const [expanded, setExpanded] = useState(false);

  return (
    <div className="border-b border-[#2d2d2d]">
      <button
        onClick={() => setExpanded(!expanded)}
        className="w-full flex items-center gap-2 px-3 py-1.5 text-[11px] font-semibold text-[#ccc] hover:bg-[#2a2d2e] transition-colors"
      >
        {expanded ? (
          <ChevronDown className="w-3.5 h-3.5" />
        ) : (
          <ChevronRight className="w-3.5 h-3.5" />
        )}
        <Settings2 className="w-3.5 h-3.5" />
        <span>Policy & Guardrails</span>
      </button>

      {expanded && (
        <div className="px-3 pb-2 space-y-2">
          {/* Rate limit */}
          <div>
            <label className="text-[10px] text-[#888] block mb-0.5">Max actions/hour</label>
            <input
              type="number"
              value={policy.maxActionsPerHour}
              onChange={(e) => updatePolicy({ maxActionsPerHour: Number(e.target.value) })}
              className="w-full bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-0.5 text-[11px] text-white focus:border-[#007acc] outline-none"
            />
          </div>

          {/* Budget */}
          <div>
            <label className="text-[10px] text-[#888] block mb-0.5">Daily budget ($)</label>
            <input
              type="number"
              step="0.50"
              value={policy.maxDailyBudget}
              onChange={(e) => updatePolicy({ maxDailyBudget: Number(e.target.value) })}
              className="w-full bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-0.5 text-[11px] text-white focus:border-[#007acc] outline-none"
            />
          </div>

          {/* Max cost per action */}
          <div>
            <label className="text-[10px] text-[#888] block mb-0.5">Max cost per action ($)</label>
            <input
              type="number"
              step="0.10"
              value={policy.maxCostPerAction}
              onChange={(e) => updatePolicy({ maxCostPerAction: Number(e.target.value) })}
              className="w-full bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-0.5 text-[11px] text-white focus:border-[#007acc] outline-none"
            />
          </div>

          {/* Toggles */}
          <div className="space-y-1.5 pt-1">
            <ToggleRow
              label="Allow mainnet deploy"
              value={policy.allowMainnetDeploy}
              onChange={(v) => updatePolicy({ allowMainnetDeploy: v })}
              danger
            />
            <ToggleRow
              label="Allow secret access"
              value={policy.allowSecretAccess}
              onChange={(v) => updatePolicy({ allowSecretAccess: v })}
              danger
            />
            <ToggleRow
              label="Notify on every action"
              value={policy.notifyOnAction}
              onChange={(v) => updatePolicy({ notifyOnAction: v })}
            />
          </div>

          {/* Rollback window */}
          <div>
            <label className="text-[10px] text-[#888] block mb-0.5">
              Rollback window (minutes)
            </label>
            <input
              type="number"
              value={policy.rollbackWindowMinutes}
              onChange={(e) => updatePolicy({ rollbackWindowMinutes: Number(e.target.value) })}
              className="w-full bg-[#1e1e1e] border border-[#3e3e3e] rounded px-2 py-0.5 text-[11px] text-white focus:border-[#007acc] outline-none"
            />
          </div>
        </div>
      )}
    </div>
  );
}

function ToggleRow({
  label,
  value,
  onChange,
  danger = false,
}: {
  label: string;
  value: boolean;
  onChange: (v: boolean) => void;
  danger?: boolean;
}) {
  return (
    <button
      onClick={() => onChange(!value)}
      className="w-full flex items-center justify-between text-[10px] hover:bg-[#2a2d2e] px-1 py-0.5 rounded transition-colors"
    >
      <span className={cn('text-[#ccc]', danger && value && 'text-red-300')}>{label}</span>
      <div
        className={cn(
          'w-6 h-3.5 rounded-full transition-colors relative',
          value ? (danger ? 'bg-red-500' : 'bg-green-500') : 'bg-[#555]',
        )}
      >
        <div
          className={cn(
            'absolute top-0.5 w-2.5 h-2.5 rounded-full bg-white transition-transform',
            value ? 'translate-x-3' : 'translate-x-0.5',
          )}
        />
      </div>
    </button>
  );
}

/* ================================================================== */
/*  Main Component                                                    */
/* ================================================================== */

export default function AiGovernancePanel() {
  const killSwitch = useAiAgentStore((s) => s.policy.killSwitch);

  return (
    <div className="flex flex-col h-full">
      {/* Kill switch warning */}
      {killSwitch && (
        <div className="px-3 py-2 bg-red-500/10 border-b border-red-500/20 flex items-center gap-2">
          <ShieldOff className="w-4 h-4 text-red-400 shrink-0" />
          <div>
            <p className="text-[11px] text-red-400 font-semibold">Kill Switch Engaged</p>
            <p className="text-[9px] text-red-400/70">
              All AI agent actions are blocked. Re-enable to resume.
            </p>
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {/* Tier selector */}
        <TierSelector />

        {/* Agent status */}
        <AgentStatusBar />

        {/* Pending approvals */}
        <PendingApprovalsSection />

        {/* Workflows */}
        <WorkflowsSection />

        {/* Action history */}
        <ActionHistorySection />

        {/* Policy configuration */}
        <PolicySection />

        {/* Security notice */}
        <div className="px-3 py-3">
          <div className="px-2.5 py-2 rounded bg-[#1e1e1e] border border-[#2d2d2d]">
            <div className="flex items-center gap-1.5 mb-1">
              <Lock className="w-3 h-3 text-[#666]" />
              <span className="text-[10px] font-semibold text-[#888]">Security Model</span>
            </div>
            <ul className="text-[9px] text-[#666] space-y-0.5">
              <li>Every AI action is logged to the audit trail</li>
              <li>AI acts as a virtual org member with RBAC</li>
              <li>High-risk actions always require human approval</li>
              <li>Kill switch immediately halts all agent operations</li>
              <li>Completed actions can be rolled back within the configured window</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
